<script lang="ts" setup>
import { tpgIconEdit } from '@tpg/grammar-web-icons';
import { computed, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { GrammarQuaternaryButton } from '@nora-monorepo/grammar-ui';
import type { MessageAttachmentDTO } from '@nora-monorepo/nora-api-client';
import {
  hoverBgPrimary,
  prettyPrintFileSize,
  textPrimary,
} from '@nora-monorepo/ui-shared';

import AttachmentGallery from '#nora-chat-ui/components/open-conversations/messages/message-attachments/AttachmentGallery.vue';
import MessageAttachmentPreview from '#nora-chat-ui/components/open-conversations/messages/message-attachments/message-attachment-preview/MessageAttachmentPreview.vue';
import { useChatUserStore } from '#nora-chat-ui/stores/user-store';
import { chatCan } from '#nora-chat-ui/utils/permission';

interface AttachmentRowProps {
  attachment: MessageAttachmentDTO;
}

const props = defineProps<AttachmentRowProps>();

defineEmits<{
  edit: [];
}>();

const userStore = useChatUserStore();
const { d } = useI18n();

const { attachment } = toRefs(props);

const isGalleryOpen = ref(false);

/**
 * Whether the current user is allowed to rename this attachment.
 */
const canEditFilename = computed(() => {
  if (
    chatCan(
      'update:chat.conversation.message.attachment',
      attachment.value.message?.conversationId,
    )
  ) {
    return true;
  }

  const isOwnAttachment
    = attachment.value.message?.sentBy.id === userStore.user?.id;

  return (
    isOwnAttachment && chatCan('update:chat.conversation.message.attachment.mine')
  );
});
</script>

<template>
  <div
    class="py-3 rounded-sm flex flex-row align-center"
    :class="[textPrimary, hoverBgPrimary]"
  >
    <div class="m-3 w-[80px]">
      <MessageAttachmentPreview
        v-if="attachment.message?.conversationId && attachment.messageId"
        :attachment="attachment"
        :conversation-id="attachment.message.conversationId"
        :message-id="attachment.messageId"
        :is-my-message="true"
        :hide-meta-data="true"
        :max-size="80"
        @open="isGalleryOpen = true"
      />
      <AttachmentGallery
        v-if="isGalleryOpen && attachment.message?.conversationId && attachment.messageId"
        :attachment="attachment"
        :conversation-id="attachment.message.conversationId"
        :message-id="attachment.messageId"
        @close="isGalleryOpen = false"
      />
    </div>

    <div class="flex flex-col my-auto">
      <div class="flex">
        <span class="break-all">{{ attachment.fileName }}</span>
      </div>

      <div v-if="attachment.createdAt" class="truncate text-sm">
        {{ d(attachment.createdAt, 'long') }}
      </div>

      <div class="text-sm">
        {{ prettyPrintFileSize(attachment.size) }}
      </div>
    </div>

    <GrammarQuaternaryButton
      v-if="canEditFilename"
      :icon-template="tpgIconEdit"
      class="ml-auto self-center mr-3"
      @click="$emit('edit')"
    />
  </div>
</template>
